import React from 'react';
import { useTranslation } from 'react-i18next';
import FormSelect from '../../../../../../antd/form/FormSelect/FormSelect';
import { required } from '../../../../../../antd/form/FormValidation.js';
import FormButton from '../../../../../../antd/form/FormButton/FormButton';
import FormButtonsContainer from '../../../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import Form from '../../../../../../antd/form/Form/Form';
import { CollapseBlock, Spinner } from '../../../../../../common/index';
import FormTextField from '../../../../../../antd/form/FormTextField/FormTextField';
import FormRadioButton from '../../../../../../antd/form/FormRadioButton/FormRadioButton';
import { RangeDatePicker } from '../../../../../../common/form/FormElements';
import FormCustomRating from '../../../../../../antd/form/FormCustomRating/FormCustomRating';
import RatingTable from '../../../../../../antd/form/RatingTable/RatingTable';
import FormTextArea from '../../../../../../antd/form/FormTextArea/FormTextArea';
import moment from 'moment';

const ErasmusQuestionnaireForm = ({ onSubmit, initials, isPreview }) => {
  const { t } = useTranslation();

  const programOptions = [
    { value: 1, label: '041 - Business and administration' },
    { value: 2, label: '061 - Information and Communication Systems' },
  ];

  const transportOptions = [
    { value: 1, label: t('lietadlo') },
    { value: 2, label: t('vlak') },
    { value: 3, label: t('autobus') },
    { value: 4, label: t('lietadlo') },
    { value: 5, label: t('vlastné auto') },
    { value: 6, label: t('zdieľané auto') },
  ];

  const customIcons = {
    1: '😞',
    2: '😐',
    3: '🙂',
    4: '😊',
    5: '😍',
  };

  const customHousingIcons = {
    1: '⛺',
    2: '🏚️',
    3: '🏡',
    4: '🏨',
    5: '🏰',
  };

  const customTimeIcons = {
    1: '⏰ ',
    2: '⌛',
    3: '🌴',
    4: '🎉',
    5: '🏖️',
  };

  const ratingSubjectsRows = [
    {
      key: 'subjects_rating',
      category: t(
        'Ako ste celkovo boli spokojní s predmetmi, ktoré ste absolvovali v zahraničí? (metódy výučby)',
      ),
    },
    {
      key: 'environment_interaction',
      category: t(
        'Ako ste boli spokojní s vyučovacím prostredím a interakciou s učiteľmi?',
      ),
    },
    {
      key: 'domestic_students',
      category: t('Ako ste boli spokojní s interakciou s domácimi študentmi?'),
    },
    {
      key: 'foreign_students',
      category: t(
        'Ako ste boli spokojní s interakciou s inými zahraničnými študentmi?',
      ),
    },
  ];

  const ratingMobilityRows = [
    {
      key: 'housing_rating',
      category: t('Ubytovanie'),
      customIcons: customHousingIcons,
    },
    {
      key: 'city_rating',
      category: t('Mesto'),
      customIcons: customIcons,
    },
    {
      key: 'free_time_rating',
      category: t('Voľnočasové aktivity'),
      customIcons: customTimeIcons,
    },
    {
      key: 'food_rating',
      category: t('Jedlo'),
      customIcons: customIcons,
    },
    {
      key: 'study_rating',
      category: t('Štúdium'),
      customIcons: customIcons,
    },
    {
      key: 'finance_rating',
      category: t('Financie'),
      customIcons: customIcons,
    },
    {
      key: 'transport_rating',
      category: t('Transport'),
      customIcons: customIcons,
    },
    {
      key: 'acquaintance_domestic_students',
      category: t('Možnosti spoznať iných zahraničných študentov'),
    },
    {
      key: 'acquaintance_foreign_students',
      category: t('Možnosti spoznať domácich študentov'),
    },
  ];

  const initialValues = {
    ...initials,
    subjectsRating: {
      subjects_rating: {
        rating: initials.subjects_rating,
      },
      environment_interaction: {
        rating: initials.environment_interaction,
      },
      domestic_students: {
        rating: initials.domestic_students,
      },
      foreign_students: {
        rating: initials.foreign_students,
      },
    },
    mobilityRatings: {
      housing_rating: {
        rating: initials.housing_rating,
      },
      city_rating: {
        rating: initials.city_rating,
      },
      free_time_rating: {
        rating: initials.free_time_rating,
      },
      food_rating: {
        rating: initials.food_rating,
      },
      study_rating: {
        rating: initials.study_rating,
      },
      finance_rating: {
        rating: initials.finance_rating,
      },
      transport_rating: {
        rating: initials.transport_rating,
      },
      acquaintance_domestic_students: {
        rating: initials.acquaintance_domestic_students,
      },
      acquaintance_foreign_students: {
        rating: initials.acquaintance_foreign_students,
      },
    },
    duration: [
      initials.mobility_start ? moment(initials.mobility_start) : null,
      initials.mobility_end ? moment(initials.mobility_end) : null,
    ],
  };

  if (!initials) {
    return <Spinner />;
  }
  return (
    <Form onFinish={onSubmit} initialValues={initialValues}>
      <CollapseBlock title={t('Základné informácie')} collapsible="false">
        <FormRadioButton
          options={programOptions}
          name={'study_field'}
          input={true}
          label={'1. ' + t('Študijný odbor na mobilite')}
          required={true}
          disabled={isPreview}
        />
        <FormTextField
          label={'2. ' + t('Študijný program absolvovaný v zahraničí')}
          name={'study_program'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <RangeDatePicker
          label={'3. ' + t('Trvanie mobility')}
          name={'duration'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormSelect
          label={
            '4. ' +
            t(
              'Boli na webe zahraničnej univerzity všetky potrebné informácie o tom, ako sa prihlásiť na mobilitu na nej?',
            )
          }
          name={'web_info'}
          options={[
            { value: 'áno', label: 'áno' },
            { value: 'nie', label: 'nie' },
          ]}
          rules={[required(t)]}
          disabled={isPreview}
        />
      </CollapseBlock>
      <CollapseBlock
        title={t('Čo je dobré vedieť pred vycestovaním?')}
        collapsible="false"
      >
        <FormTextArea
          label={
            '5. ' +
            t(
              'Prečo ste si pre svoju mobilitu vybrali konkrétnu univerzitu/fakultu? Čím Vás zaujala?',
            )
          }
          name={'reason_for_choosing'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <RatingTable
          label={'6.' + 'Spokojnosť s predmetmi v zahraničí'}
          name={'subjectsRating'}
          rows={ratingSubjectsRows}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '7. ' + t('Vysvetlite svoje odpovede v otázke číslo 6, prosím:')
          }
          name={'subjects_explanation'}
          rules={[required(t)]}
          disabled={isPreview}
        />

        <FormTextArea
          label={
            '8. ' +
            t(
              'Ktoré predmety na zahraničnej univerzite Vás najviac oslovili a prečo? Ktoré predmety by ste odporučili ďalším študentom z FRI UNIZA, ktorí pôjdu študovať na rovnakú zahraničnú univerzitu?',
            )
          }
          name={'subjects_recommendation'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '9. ' +
            t(
              'Okomentujte interakciu, komunikáciu s vyučujúcimi na zahraničnej univerzite?',
            )
          }
          name={'interaction_teachers'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '10. ' +
            t(
              'Ako hodnotíte celkovo ponuku predmetov na zahraničnej univerzite a organizáciu štúdia v zahraničí?',
            )
          }
          name={'subjects_offer'}
          rules={[required(t)]}
          disabled={isPreview}
        />
      </CollapseBlock>
      <CollapseBlock title={t('Praktické rady')} collapsible="false">
        <FormRadioButton
          options={transportOptions}
          name={'means_of_transport'}
          input={true}
          label={'11. ' + t('Akými dopravnými prostriedkami ste cestovali?')}
          required={true}
          disabled={isPreview}
        />
        <FormSelect
          label={
            '12. ' + t('Odporúčate takýto spôsob cestovania aj iným študentom?')
          }
          name={'travel_recommendation'}
          options={[
            { value: 'áno', label: 'áno' },
            { value: 'nie', label: 'nie' },
          ]}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={'13. ' + t('Aký bol príchod do zahraničia? Prvé pocity...')}
          name={'arrival_abroad'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <RatingTable
          label={
            '14. ' + 'Ako by ste ohodnotili nasledujúce aspekty Vašej mobility?'
          }
          name={'mobilityRatings'}
          rows={ratingMobilityRows}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormSelect
          label={
            '15. ' +
            t(
              'Bol niekto, kto sa Vás ujal a komunikoval s Vami, ešte pred cestou do zahraničia?',
            )
          }
          name={'buddy'}
          options={[
            { value: 'áno', label: 'áno' },
            { value: 'nie', label: 'nie' },
          ]}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormSelect
          label={
            '16. ' +
            t(
              'Zúčastnili ste sa tzv. Orientation (Welcome) Days (Week) - Dozvedeli ste sa všetko čo ste potrebovali na úspešný začiatok štúdia v zahraničí?',
            )
          }
          name={'orientation_welcome_days_week'}
          options={[
            { value: 'áno', label: 'áno' },
            { value: 'nie', label: 'nie' },
          ]}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '17. ' +
            t(
              'Aké ste mali náklady na administratívny poplatok (napr. v Nemecku - SemesterTicket)?',
            )
          }
          name={'costs_administration'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '18. ' +
            t(
              'Aké ste mali ďalšie náklady na poplatky za prihlášku na univerzitu (ak existujú)?',
            )
          }
          name={'costs_application'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '19. ' +
            t('Aké ste mali náklady na cestu do zahraničia a naspäť domov?')
          }
          name={'costs_travel'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={'20. ' + t('Aké ste mali náklady na cestovné poistenie?')}
          name={'costs_travel_insurance'}
          rules={[required(t)]}
          disabled={isPreview}
        />

        <FormTextArea
          label={'21. ' + t('Aké ste mali náklady na ubytovanie?')}
          name={'costs_housing'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '22. ' +
            t('Aké ste mali náklady na jedlo (napr. jeden obed v menze)?')
          }
          name={'costs_food'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '23. ' +
            t(
              'Aké ste mali náklady na ostatné výdavky (voľný čas, turistika, iné miestne výlety, nepredvídané udalosti atď.)?',
            )
          }
          name={'other_costs'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '24. ' +
            t(
              'Ktorá zložka nákladov bola najväčšia, ktoré náklady boli nevyhnutné?',
            )
          }
          name={'biggest_costs'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '25. ' +
            t('Aké boli CELKOVÉ odhadované náklady na Váš pobyt v zahraničí?')
          }
          name={'total_costs'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormSelect
          label={
            '26. ' +
            t(
              'Bolo náročné nájsť ubytovanie? Ponúkala univerzita nejaké možnosti?',
            )
          }
          name={'search_for_accommodation'}
          options={[
            { value: 'áno', label: 'áno' },
            { value: 'nie', label: 'nie' },
          ]}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={'27. ' + t('Napíšte prosím viac o Vašom ubytovaní.')}
          name={'about_housing'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '28. ' +
            t(
              'Mali ste nejaké problémy s financiami: obmedzenia použitia platobných kariet, spôsoby platby (hotovosť, bezhotovostne, ...)?',
            )
          }
          name={'payment_problems'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '29.' +
            t(
              'Mali ste nejaké problémy s používaním elektrických zariadení: zásuvky, zástrčky, potreba adaptérov, ...?',
            )
          }
          name={'electronic_devices_problems'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormCustomRating
          label={
            '30. ' +
            t('Ako hodnotíte životné náklady na pobyt vo vybranej krajine?')
          }
          name={'cost_of_living'}
          rules={[required(t)]}
          disabled={isPreview}
        />

        <FormTextArea
          label={
            '31. ' +
            t(
              'Museli ste vybavovať nejaké administratívne záležitosti v krajine? Čo všetko ste museli riešiť a zariaďovať v zahraničí? Ako si vyriešili dlhší pobyt v zahraničí - prihlásili ste sa na prechodný pobyt alebo ste si spravili výlet za hranice v priebehu 90 dní, aby to nebolo potrebné?',
            )
          }
          name={'other_administrative_responsibilities'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '32. ' +
            t(
              'Aká bola komunikácia s miestnymi obyvateľmi v zahraničí? Mali ste možnosť stretnúť sa alebo porozprávať sa? Stačilo komunikovať po anglicky? Akú metódu ste použili na odstránenie jazykovej a kultúrnej bariéry?',
            )
          }
          name={'comunication_with_locals'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '33. ' +
            t(
              'Aké to bolo v inej krajine? Čo sa Vám páčilo? Aká bola Vaša skúsenosť? Išli by ste žiť alebo ďalej študovať v do tej istej krajiny?',
            )
          }
          name={'satisfaction_with_country'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '34. ' + t('Vyskytlo sa niečo neočakávané v každodennom živote?')
          }
          name={'unexpected_life_situation'}
          rules={[required(t)]}
          disabled={isPreview}
        />

        <FormTextArea
          label={
            '35. ' +
            t(
              'Zúčastnili ste sa nejakých integračných aktivít organizovaných ESN? Ak áno, prečo a čo Vám to dalo?',
            )
          }
          name={'esn_activity'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '36. ' +
            t(
              'V prípade krízovej situácie (zdravotné, prírodné katastrofy, bezpečnosť) - ako to zvládla hostiteľská univerzita? napr.: komunikácia, ústretovosť, sledovanie atď. Ako ste to riešili Vy?',
            )
          }
          name={'crisis_situation'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={
            '37. ' +
            t(
              'Aké bolo ukončenie Vašej mobility? Mali ste všetky potrebné dokumenty? Vedeli ste, čo máte robiť a čo potrebujete pred odchodom? Mali ste s kým komunikovať pred odchodom?',
            )
          }
          name={'end_of_mobility'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormTextArea
          label={'38. ' + t('Máte nejaké osobné hodnotenie a návrhy?')}
          name={'summarization'}
          rules={[required(t)]}
          disabled={isPreview}
        />
        <FormCustomRating
          label={
            '39. ' +
            t(
              'Ako hodnotíte skúsenosť so štúdiom v zahraničí celkovo (vrátane administratívy spojenej s mobilitou)?',
            )
          }
          name={'cost_of_living'}
          rules={[required(t)]}
          customIcons={customIcons}
          disabled={isPreview}
        />
      </CollapseBlock>
      {isPreview ? null : (
        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'erasmusApplications'}>
            {t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      )}
    </Form>
  );
};

export default ErasmusQuestionnaireForm;
